import type { Facility } from '../../facilities/index.js'
import type { FacilityAdministrator, UpdateFacilityAdministratorInput } from '../index.js'
import { LoadFailure } from '../../core/index.js'
import {
  useDeleteFacilityAdministrator,
  useFacilityAdministrators,
  useGenerateFacilityAdministratorResetLink,
  useInviteFacilityAdministrator,
  useLoginInvitationActions,
  useLoginInvitations,
  useUpdateFacilityAdministrator,
} from '../hooks.js'
import { FacilityAdministratorControl } from './FacilityAdministratorControl.js'

interface FacilityAdministratorsSectionProps {
  clientId: string | undefined
  facility: Facility
}

/**
 * One facility's administrators for the operator: invite, edit, rotate,
 * send a reset link, and remove - from this facility, or entirely when it
 * was their last. The facility list and the Configuration page's Details
 * tab both show it; the queries are shared by key, so a list of many
 * facilities still reads the client's administrators once.
 */
export function FacilityAdministratorsSection({ clientId, facility }: FacilityAdministratorsSectionProps) {
  const administratorsQuery = useFacilityAdministrators(clientId)
  // Invitations rather than passwords (invitations plan phase 2): the person sets their own from the email.
  const createMutation = useInviteFacilityAdministrator(clientId)
  const invitationsQuery = useLoginInvitations(clientId, 'facility-administrators', true)
  const invitationActions = useLoginInvitationActions(clientId, 'facility-administrators')
  const updateMutation = useUpdateFacilityAdministrator(clientId)
  const deleteMutation = useDeleteFacilityAdministrator(clientId)
  const resetLinkMutation = useGenerateFacilityAdministratorResetLink(clientId)

  async function handleUpdate(administratorId: string, input: UpdateFacilityAdministratorInput): Promise<string | undefined> {
    const updated = await updateMutation.mutateAsync({ administratorId, input })
    return updated.password
  }

  // Unassigns just this facility, leaving the administrator's access to
  // every other facility untouched - only deletes the administrator outright
  // when this was their last remaining facility (an administrator can never
  // have an empty facilityIds list).
  async function handleRemove(administrator: FacilityAdministrator, facilityId: string): Promise<void> {
    const remainingFacilityIds = administrator.facilityIds.filter((id) => id !== facilityId)
    if (remainingFacilityIds.length === 0) {
      await deleteMutation.mutateAsync(administrator.administratorId)
    } else {
      await updateMutation.mutateAsync({ administratorId: administrator.administratorId, input: { facilityIds: remainingFacilityIds } })
    }
  }

  async function handleGenerateResetLink(administratorId: string): Promise<string> {
    const { resetUrl } = await resetLinkMutation.mutateAsync(administratorId)
    return resetUrl
  }

  if (administratorsQuery.isPending) {
    return <span className="text-[11px] text-ink-mid">Loading administrator…</span>
  }
  if (administratorsQuery.isError) {
    return <LoadFailure size="xs" message="Failed to load administrator." onRetry={() => void administratorsQuery.refetch()} retrying={administratorsQuery.isFetching} />
  }
  return (
    <FacilityAdministratorControl
      facility={facility}
      administratorsForFacility={administratorsQuery.data.filter((administrator) => administrator.facilityIds.includes(facility.facilityId))}
      onCreate={(input) => createMutation.mutateAsync(input)}
      invitations={invitationsQuery.data ?? []}
      onResendInvitation={(invitationId) => invitationActions.resend.mutateAsync(invitationId)}
      onRevokeInvitation={(invitationId) => invitationActions.revoke.mutateAsync(invitationId)}
      onUpdate={handleUpdate}
      onRemove={handleRemove}
      onGenerateResetLink={handleGenerateResetLink}
      isCreating={createMutation.isPending}
      isUpdating={updateMutation.isPending}
      isRemoving={deleteMutation.isPending || updateMutation.isPending}
      isGeneratingResetLink={resetLinkMutation.isPending}
      createError={createMutation.isError ? createMutation.error.message : null}
    />
  )
}
